import _ from "lodash";
import ko from "knockout";

//Constants declaration
const TEMPLATE = "templates/widgets/inlineConfirmation";
const DEFAULT_OK_KEY = "inline.confirmation.btn.label.ok";
const DEFAULT_CANCEL_KEY = "inline.confirmation.btn.label.cancel";

/**
 * Validates and merges default properties with bindProperties. Returns valid binding properties
 * @param {Object} bindProperties - properties passed in by consumer of api
 * @param {Object} defaultProperties - default properties
 * @returns {Object} valid properties if no errors are thrown due to missing or invalid properties
 */
const getBindingProperties = function (bindProperties, defaultProperties) {
    const errorMessage =
        "inlineConfirmation binding error. Must specify binding properties: messageKey, show, and onConfirm";

    if (!_.isPlainObject(bindProperties)) {
        throw new Error(errorMessage);
    }

    const prop = _.assign(defaultProperties, bindProperties);

    if (!prop.show || !prop.onConfirm || !prop.messageKey) {
        throw new Error(errorMessage);
    }
    return prop;
};

/**
 * The inlineConfirmation binding will display an inline message with confirmation button with optional additional buttons and an optional cancel button
 * Sample usage
 *  data-bind= "inlineConfirmation:{    messageKey      : 'my.string.key',       // The message key for the string to display as the confirmation message/question that will be displayed to the user.
 *                                      messageData     : myMessageData (Optional)  // The data object used to for replacements in messageKey
 *                                      show            :  myViewModelProperty,  // View model observable property  (boolean) that triggers when the confirmation is shown/hidden
 *                                      customButtons   :  array                 // Array of objects of the form { localizationKey: key, onClick: function }
 *                                      onConfirm       :  myViewModelFunction   // View model function to run when the OK button is clicked
 *                                      confirmLabelKey :  'my.string.key' (Optional)   // Localization key to use for the onConfirm button
 *                                      onCancel        :  myViewModelFunction (Optional)   // View model function to run when the Cancel button is clicked
 *                                      cancelLabelKey :  'my.string.key' (Optional)   // Localization key to use for the onCancel button
 *                                      isWaiting       :  actionInProgress      // View model observable property -  Boolean. When true the spinner will show on the button for asynchronous actions
 *  }"
 */

ko.bindingHandlers.inlineConfirmation = {
    init: function (element, valueAccessor) {
        const bindProperties = ko.unwrap(valueAccessor());
        const defaultProperties = {
            isWaiting: ko.observable(false),
        };

        const properties = getBindingProperties(
            bindProperties,
            defaultProperties
        );

        _.forEach(properties.customButtons, function (customButton) {
            customButton.isWaiting =
                customButton.isWaiting || ko.observable(false);
        });

        const confirmationViewModel = {
            customButtons: properties.customButtons,
            onOkClick: properties.onConfirm,
            confirmLabelKey: properties.confirmLabelKey || DEFAULT_OK_KEY,
            onCancelClick() {
                if (properties.onCancel) {
                    properties.onCancel();
                }
                properties.show(false);
            },
            cancelLabelKey: properties.cancelLabelKey || DEFAULT_CANCEL_KEY,
            messageKey: properties.messageKey,
            messageData: properties.messageData,
            isWaiting: properties.isWaiting,
        };

        ko.renderTemplate(
            TEMPLATE,
            confirmationViewModel,
            { afterRender: null },
            element,
            "replaceChildren"
        );

        return { controlsDescendantBindings: true };
    },
};
